/**
 * The 2x2's presentation layer — emoji, label, support line and hue per quadrant.
 *
 * Shared by the list and the map so a row and its bubble can never disagree.
 * Copy is verbatim from the approved handoff (`Tu tráfico.dc.html`, option 1b);
 * hues follow the merchant convention — green is reserved for the one genuine
 * winner, amber is the leak hue, blue is positive-but-neutral.
 */

import type { Quadrant, TrafficCampaignRow } from '@/api/traffic'
import { fmtInt, fmtMoney } from '@/lib/formatEsAr'

export interface QuadrantMeta {
  emoji: string
  label: string
  /** The explanatory line. Lives in the reference card and the share payload. */
  line: string
  /** Lower-case fragment for the channel roll-up: "En general: le interesa, no compra". */
  rollup: string
  stroke: string
  fill: string
}

export const QUADRANT: Record<Quadrant, QuadrantMeta> = {
  valioso: {
    emoji: '🟢',
    label: 'Te trae compradores',
    line: 'La gente que llega mira y compra.',
    rollup: 'te trae compradores',
    stroke: '#00935b',
    fill: 'rgba(0,147,91,.15)',
  },
  interesa_no_cierra: {
    emoji: '🟡',
    label: 'Le interesa, no compra',
    line: 'Llegan, miran tus productos, pero no terminan comprando.',
    rollup: 'le interesa, no compra',
    stroke: '#bb5d08',
    fill: 'rgba(187,93,8,.12)',
  },
  llega_decidido: {
    emoji: '✅',
    label: 'Llega decidido',
    line: 'Compra rápido, sin dar muchas vueltas por tu tienda.',
    rollup: 'llega decidido',
    stroke: '#0059d5',
    fill: 'rgba(0,89,213,.12)',
  },
  no_sirve: {
    emoji: '🔴',
    label: 'Entra y se va',
    line: 'La mayoría se va sin mirar nada.',
    rollup: 'entra y se va',
    stroke: '#b91c1c',
    fill: 'rgba(185,28,28,.12)',
  },
  sin_datos: {
    emoji: '⏳',
    label: 'Todavía no alcanza',
    line: 'Pocas visitas para decirte algo confiable. Volvé a mirar en unos días.',
    rollup: 'todavía sin datos',
    stroke: '#b0b0b0',
    fill: 'rgba(0,0,0,.06)',
  },
}

/**
 * "N compran" per 100 visits. Below half a percent the integer rounds to 0, and "0 de cada 100
 * compran" next to a campaign with sales claims nobody bought, so a rate that rounds away says
 * "menos de 1" instead. A real zero stays 0.
 */
export function boughtPer100(conversionRate: number): { count: string; verb: string } {
  const bought = Math.round(conversionRate * 100)
  if (bought === 0 && conversionRate > 0) return { count: 'menos de 1', verb: 'compra' }
  return { count: String(bought), verb: bought === 1 ? 'compra' : 'compran' }
}

/**
 * The row's one numeric sentence, shared by the list and the shareable card so a
 * screenshot and a forwarded text can never say different things.
 *
 * Rates are always "de cada 100" — never a percentage — matching the banner-results
 * grammar. Three shapes, by how much the row actually earned the right to say:
 * fully measured, behaviour-only (half-placed), and nothing yet.
 */
export function campaignMetricLine(row: TrafficCampaignRow): string {
  const visits = `${fmtInt(row.sessions)} visitas`
  if (row.engagement_rate == null) {
    return `${visits} · Pocas visitas para decirte algo confiable. Volvé a mirar en unos días.`
  }
  // Each segment appears only if its own number is known. They are measured independently
  // and can ramp at different times, so the sentence must never pair a known conversion
  // count with an unknown price rendered as "$ 0" — that reads as a contradiction.
  const looked = Math.round(row.engagement_rate * 100)
  let behaviour = `de cada 100, ${looked} se quedan mirando`
  if (row.conversion_rate != null) {
    const { count, verb } = boughtPer100(row.conversion_rate)
    behaviour += ` y ${count} ${verb}`
  }
  const parts = [visits, behaviour]
  if (row.value_per_visit != null) parts.push(`${fmtMoney(row.value_per_visit)} por visita`)
  return parts.join(' · ')
}

/**
 * Channel palette for the map.
 *
 * On the chart, quadrant colour carried no information — the quadrant IS the position, so
 * colouring by it said the same thing twice. Channel is the dimension position can't show,
 * and it's the one a merchant asks first ("where is my Paid Search?"). The list keeps the
 * quadrant stoplight; the chart's tooltip names the quadrant, so nothing is lost.
 *
 * The GA4 channel set is fixed and known, so assignment is an EXPLICIT map rather than a
 * hash of the name: hashing collided on 4 of the 8 real channels (Direct/Paid Social,
 * Email/Paid Search/Paid Other, Referral/Organic Search), which would have made two
 * different channels indistinguishable on the very chart meant to tell them apart.
 * Paid/organic pairs share a hue family so the split reads at a glance.
 */
const CHANNEL_COLORS: Array<{ stroke: string; fill: string }> = [
  { stroke: '#0059d5', fill: 'rgba(0,89,213,.14)' },   // blue
  { stroke: '#00935b', fill: 'rgba(0,147,91,.16)' },   // green
  { stroke: '#bb5d08', fill: 'rgba(187,93,8,.14)' },   // orange
  { stroke: '#7c3aed', fill: 'rgba(124,58,237,.14)' }, // violet
  { stroke: '#0e7490', fill: 'rgba(14,116,144,.14)' }, // teal
  { stroke: '#be185d', fill: 'rgba(190,24,93,.13)' },  // pink
  { stroke: '#a16207', fill: 'rgba(161,98,7,.14)' },   // gold
  { stroke: '#4b5563', fill: 'rgba(75,85,99,.13)' },   // slate
]

const CHANNEL_COLOR_INDEX: Record<string, number> = {
  'Paid Search': 0,
  'Organic Search': 4,
  'Paid Social': 3,
  'Organic Social': 5,
  Email: 1,
  Direct: 7,
  Referral: 6,
  'Paid Other': 2,
}

export function channelColor(channel: string): { stroke: string; fill: string } {
  const known = CHANNEL_COLOR_INDEX[channel]
  if (known != null) return CHANNEL_COLORS[known]
  // Unknown channel (rule changes, future taxonomy) — stable fallback, never a crash.
  let hash = 0
  for (let i = 0; i < channel.length; i++) hash = (hash * 31 + channel.charCodeAt(i)) >>> 0
  return CHANNEL_COLORS[hash % CHANNEL_COLORS.length]
}

/**
 * Meta (and sometimes Google) let you template the UTM as either the campaign NAME or its
 * ID. Plenty of accounts are set to `{{campaign.id}}`, so `utm_campaign` arrives as a bare
 * 15–18 digit number that means nothing to a merchant and cannot be shortened into
 * anything meaningful.
 *
 * We can't resolve an ID to a name without an ad-platform integration (explicitly out of
 * scope), so we do the honest thing instead: mark it as an identifier, keep it complete so
 * it can be pasted into the ads manager, and tell them how to fix it at the source.
 */
export function isOpaqueCampaignId(campaign: string): boolean {
  const bare = campaign.replace(/[\s_-]/g, '')
  return /^\d{8,}$/.test(bare)
}

/** What to call this campaign in a sentence. IDs get a noun so they don't read as a name. */
export function campaignDisplayName(campaign: string): string {
  return isOpaqueCampaignId(campaign) ? `la campaña ${campaign}` : `«${campaign}»`
}

/**
 * Campaign names are machine-generated and long ("[ADV] Ventas - Remarketing -
 * Catálogo dinámico - AR - Compradores 30d"). The map needs something that fits
 * next to a bubble: drop the bracketed prefix and keep the two most specific
 * segments, which is where the human-meaningful part lives.
 */
export function shortCampaignName(campaign: string): string {
  const stripped = campaign.replace(/^\s*\[[^\]]*\]\s*/, '')
  const parts = stripped.split(/\s+-\s+/).filter(Boolean)
  if (parts.length <= 2) return stripped
  return parts.slice(1, 3).join(' - ')
}

/**
 * Chart labels for a set of campaigns, keyed by campaign string.
 *
 * Opaque IDs used to get NO label at all, on the reasoning that an 18-digit number says
 * nothing. That was the wrong trade: it left the biggest spender on the chart anonymous
 * purely because its account templates `{{campaign.id}}`, so size — the thing the merchant
 * most needs to find — decided nothing and tagging style decided everything. An ugly name
 * is a reason to SHORTEN a label, never to withhold identity.
 *
 * IDs are shortened to their last digits, using the shortest suffix that is unique across
 * the set so two bubbles can never carry the same label. The full string stays in the
 * tooltip and the list, where it can be copied.
 */
export function campaignChartLabels(campaigns: string[]): Map<string, string> {
  const ids = campaigns.filter(isOpaqueCampaignId)
  const bare = (c: string) => c.replace(/[\s_-]/g, '')

  // Six digits is the floor: enough for the label to be recognisable against the same
  // campaign in the list, short enough to sit under a bubble. Grows only to break ties.
  let suffix = 6
  const longest = Math.max(0, ...ids.map((c) => bare(c).length))
  while (suffix < longest) {
    const seen = new Set(ids.map((c) => bare(c).slice(-suffix)))
    if (seen.size === ids.length) break
    suffix += 1
  }

  const out = new Map<string, string>()
  for (const c of campaigns) {
    out.set(c, isOpaqueCampaignId(c) ? `…${bare(c).slice(-suffix)}` : shortCampaignName(c))
  }
  return out
}
